import slugify from 'slugify';

/**
 * Default options for slug comparison
 */
const DEFAULT_SLUGIFY_OPTIONS: Parameters<typeof slugify>[1] = {
  lower: true,
  remove: /[^\w\s]/g,
};

/** Slug options that also drop separators, so `Luxury Vinyl`, `luxury-vinyl` and `luxuryvinyl` slugify alike. */
export const COMPACT_SLUGIFY_OPTIONS: Parameters<typeof slugify>[1] = {
  lower: true,
  replacement: '',
  remove: /[^\w\s-]|[_-]/g,
};

export function Slugify(
  str: string,
  slugifyOptions: Parameters<typeof slugify>[1] = DEFAULT_SLUGIFY_OPTIONS
): string {
  return slugify(str, slugifyOptions);
}
/**
 * Checks if two strings are equal after being slugified.
 *
 * @param a The first string to compare.
 * @param b The second string to compare.
 * @returns Returns true if the slugified strings are equal, false otherwise.
 */
export function SlugEquals(
  a: string,
  b: string,
  slugifyOptions: Parameters<typeof slugify>[1] = DEFAULT_SLUGIFY_OPTIONS
): boolean {
  return slugify(a, slugifyOptions) === slugify(b, slugifyOptions);
}

/**
 * Returns the intersection of two arrays based on slugified equality.
 *
 * @param a The first array of strings.
 * @param b The second array of strings.
 * @returns The intersection of the two arrays based on slugified equality.
 */
export function SlugArraysIntersect(
  a: string[],
  b: string[],
  slugifyOptions: Parameters<typeof slugify>[1] = DEFAULT_SLUGIFY_OPTIONS
): string[] {
  // Return array items from 'a' that exist in 'b' after slugification
  return a.filter((aItem) =>
    b.some((bItem) => SlugEquals(aItem, bItem, slugifyOptions))
  );
}

/**
 * Checks a string to see if it is a valid slug
 * @param a the string to check
 * @param slugifyOptions optional slugify options to pass along
 * @returns a boolean indicating if the string is a slug or not
 */
export function IsSlug(
  a: string,
  slugifyOptions: Parameters<typeof slugify>[1] = DEFAULT_SLUGIFY_OPTIONS
): boolean {
  return Slugify(a, slugifyOptions) === a;
}
